import { Check, Copy, TriangleAlert } from "lucide-react"
import { useState } from "react"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

/**
 * Un secreto que el gateway enseña **una sola vez**: una API key recién emitida, la
 * contraseña de una persona recién dada de alta.
 *
 * Vive aquí y no dentro de una funcionalidad porque el comportamiento es el mismo en
 * los dos sitios y es **relevante para la seguridad**: no se cierra con Escape ni
 * clicando fuera, y el botón de cerrar no se habilita hasta haber copiado. Dos copias
 * de esto acabarían divergiendo, y la que divergiera sería la que deja a alguien sin su
 * credencial.
 *
 * El aviso va **antes** que el secreto, para que se lea antes de actuar y no después.
 */
export function SecretoUnaVez({
  abierto,
  titulo,
  subtitulo,
  secreto,
  tituloAviso,
  aviso,
  etiquetaCopiar,
  etiquetaPendiente,
  onCerrar,
}: {
  abierto: boolean
  titulo: string
  subtitulo?: string
  secreto: string
  //: Las etiquetas se pasan enteras y no se componen a partir de una palabra: en
  //: español el género obliga ("cópiala" una clave, "cópialo" un secreto), y una
  //: plantilla con concatenación acaba produciendo frases que nadie diría.
  tituloAviso: string
  aviso: string
  etiquetaCopiar: string
  etiquetaPendiente: string
  onCerrar: () => void
}) {
  const [copiado, setCopiado] = useState(false)

  async function copiar() {
    await navigator.clipboard.writeText(secreto)
    setCopiado(true)
  }

  function cerrar() {
    setCopiado(false)
    onCerrar()
  }

  return (
    <Dialog
      open={abierto}
      onOpenChange={(sigue) => {
        if (!sigue) cerrar()
      }}
    >
      <DialogContent
        className="sm:max-w-lg"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          {subtitulo !== undefined && <DialogDescription>{subtitulo}</DialogDescription>}
        </DialogHeader>

        <Alert variant="destructive">
          <TriangleAlert className="size-4" />
          <AlertTitle>{tituloAviso}</AlertTitle>
          <AlertDescription>{aviso}</AlertDescription>
        </Alert>

        <div className="flex items-center gap-2">
          <code className="min-w-0 flex-1 overflow-x-auto rounded-md border bg-muted px-3 py-2 font-mono text-sm">
            {secreto}
          </code>
          <Button variant="outline" size="icon" onClick={copiar} aria-label={etiquetaCopiar}>
            {copiado ? <Check className="size-4" /> : <Copy className="size-4" />}
          </Button>
        </div>

        <DialogFooter>
          <Button onClick={cerrar} disabled={!copiado}>
            {copiado ? "Hecho" : etiquetaPendiente}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
